// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import type {CopilotChatReference} from '@github-ui/copilot-chat-types'
import InputLabel from '@github-ui/input-label'
import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {useQuery} from '@github-ui/react-query'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {fetchQuery, graphql, readInlineData} from 'relay-runtime'

import type {TimelineEventTextReference} from '../../timeline-events/timeline-events-types'
import {type DraftIssue, makeReferenceFromVersionedItem} from '../content-preview-types'
import {useContentPreview} from '../ContentPreviewContext'
import type {TemplatePickerForm$data, TemplatePickerForm$key} from './__generated__/TemplatePickerForm.graphql'
import type {TemplatePickerQuery} from './__generated__/TemplatePickerQuery.graphql'
import type {
  TemplatePickerTemplate$data,
  TemplatePickerTemplate$key,
} from './__generated__/TemplatePickerTemplate.graphql'
import styles from './RepositoryPicker.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TemplatesGraphqlQuery = graphql`
  query TemplatePickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      issueForms {
        __typename
        ...TemplatePickerForm
      }
      issueTemplates {
        __typename
        ...TemplatePickerTemplate
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const FormFragment = graphql`
  fragment TemplatePickerForm on IssueForm @inline {
    __typename
    filename
    name
    description
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TemplateFragment = graphql`
  fragment TemplatePickerTemplate on IssueTemplate @inline {
    __typename
    filename
    name
    about
  }
`

type TemplatePickerItem = TemplatePickerTemplate$data | TemplatePickerForm$data

function isTemplate(item: TemplatePickerItem): item is TemplatePickerTemplate$data {
  return item.__typename === 'IssueTemplate'
}

export function useTemplatesQuery({owner, repo}: {owner: string | undefined; repo: string | undefined}) {
  const environment = useRelayEnvironment()

  return useQuery({
    queryKey: ['copilot-immersive-templates', JSON.stringify(environment), owner, repo],
    queryFn: async () => {
      if (!owner || !repo) return []

      const data = await fetchQuery<TemplatePickerQuery>(environment, TemplatesGraphqlQuery, {owner, repo}).toPromise()

      const issueTemplates = (data?.repository?.issueTemplates || []).map(node => {
        // eslint-disable-next-line no-restricted-syntax
        return readInlineData<TemplatePickerTemplate$key>(TemplateFragment, node)
      })

      const issueForms = (data?.repository?.issueForms || []).map(node => {
        // eslint-disable-next-line no-restricted-syntax
        return readInlineData<TemplatePickerForm$key>(FormFragment, node)
      })

      return [...issueTemplates, ...issueForms].sort((a, b) => {
        return a.filename.localeCompare(b.filename)
      })
    },
  })
}

function useTemplateSelectedCallback() {
  const manager = useChatManager()
  const state = useChatState()
  const {updateItem} = useContentPreview()

  return useCallback(
    async (draftIssue: DraftIssue, template: TemplatePickerItem | undefined, repositoryNwo?: string) => {
      const instructions: TimelineEventTextReference = {
        type: 'text',
        name: `timeline-event: {"type": "switch-issue-template", "markdownContent": "${
          template ? `Changing template to '${template.name}'…` : 'Removing template…'
        }"}`,
        text: template
          ? `Update the draft issue with tag \`${draftIssue.tag}\` with the \`${template.filename}\` template${
              repositoryNwo ? ` in the \`${repositoryNwo}\` repository` : ''
            }. Regenerate the title and description following the new template. Maintain all conversation context. Update the metadata to follow the new template. DO NOT change the \`tag\` or \`parentTag\` fields.`
          : `Update draft issue with tag \`${draftIssue.tag}\` to remove the template formatting. Regenerate the title and description to follow a generic format. Maintain all conversation context. DO NOT change the \`tag\` or \`parentTag\` fields.`,
      }

      const references: CopilotChatReference[] = [instructions]
      if (draftIssue.isUserEdited) {
        // Since references of a supported type get "deserialized" back as a versioned item in content preview,
        // we make sure this version contains any user updates that were made.
        // We don't update the template because the draft issue's content is still using the old/blank template
        const updatedDraftIssue = {
          ...draftIssue,
          // there's a race condition where if the repo was changed before selecting the new template,
          // the repo sometimes isn't updated in the draftIssue. Ensure the correct repo is sent:
          ...(repositoryNwo && {repository: repositoryNwo}),
          // Mark content preview items that correspond to the references as not user-edited so that they don't automatically
          // get closed when the chat reducer clears the currentReferences.
          isUserEdited: false,
        }
        updateItem(updatedDraftIssue)
        references.push(makeReferenceFromVersionedItem(updatedDraftIssue))
      }
      await manager.sendChatMessage({
        thread: getSelectedThread(state),
        content: template ? `Use the '${template.name}' template` : 'Remove the template',
        references: [...references, ...state.currentReferences],
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
        model: state.model,
      })
    },
    [manager, state, updateItem],
  )
}

export function TemplatePicker({draftIssue}: {draftIssue: DraftIssue}) {
  const {repository} = useIssueCreateMetadata()
  const {data: templates} = useTemplatesQuery({
    owner: repository?.owner.login,
    repo: repository?.name,
  })
  const buttonId = useId()

  if (templates == null || templates.length === 0) {
    return null
  }

  const templateRequired =
    draftIssue.type === 'new-issue' && repository?.isBlankIssuesEnabled != null && !repository.isBlankIssuesEnabled

  return (
    <div className={styles.container}>
      <InputLabel className={styles.label} required={templateRequired} htmlFor={buttonId}>
        Template
      </InputLabel>
      <TemplatePickerInternal
        {...{
          templates,
          draftIssue,
          repositoryNwo: repository?.nameWithOwner,
          buttonId,
        }}
      />
    </div>
  )
}

function TemplatePickerInternal({
  templates,
  draftIssue,
  repositoryNwo,
  buttonId,
}: {
  templates: TemplatePickerItem[]
  draftIssue: DraftIssue
  repositoryNwo?: string
  buttonId: string
}) {
  const [filter, setFilter] = useState<string>('')
  const filteredItems = useMemo(() => {
    if (!filter) {
      return templates
    }
    return templates?.filter(item => {
      return item.name?.toLowerCase().includes(filter.toLowerCase())
    })
  }, [templates, filter])

  const [selectedItem, setSelectedItem] = useState<TemplatePickerItem | undefined>(undefined)
  useEffect(() => {
    if (!draftIssue.template) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedItem(undefined)
      return
    }

    // Copilot will sometimes return the template with full path, rather than just the filename
    const initTemplate = draftIssue.template.split('/').findLast(Boolean)
    // Sometimes it will return the template name instead of filename as well, so we check both for a match
    const selected = templates?.find(item => item.filename === initTemplate || item.name === initTemplate)
    setSelectedItem(selected)
  }, [templates, draftIssue.template])
  const onTemplateSelected = useTemplateSelectedCallback()

  const templatePickerRef = useRef<HTMLButtonElement>(null)

  return (
    <ItemPicker
      items={filteredItems ?? []}
      initialSelectedItems={selectedItem ? [selectedItem] : []}
      placeholderText={'Search'}
      selectionVariant={'single'}
      filterItems={setFilter}
      title={'Select a template'}
      renderAnchor={(props: React.HTMLAttributes<HTMLElement>): React.ReactElement => {
        return (
          <Button id={buttonId} trailingVisual={TriangleDownIcon} {...props} ref={templatePickerRef}>
            {selectedItem ? <span>{selectedItem.name}</span> : 'Select a template'}
          </Button>
        )
      }}
      getItemKey={item => item.filename}
      convertToItemProps={item => ({
        id: item.filename,
        text: item.name,
        description: (isTemplate(item) ? item.about : item.description) || undefined,
        descriptionVariant: 'block',
        source: item,
      })}
      onSelectionChange={async ([item]) => {
        setSelectedItem(item)
        await onTemplateSelected(draftIssue, item, repositoryNwo)
      }}
      selectPanelRef={templatePickerRef}
      height={'large'}
      width={'medium'}
    />
  )
}
